import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { MdClose, MdStar, MdStarBorder, MdCheckCircle, MdFeedback, MdContentCopy, MdCheck } from "react-icons/md";
import { API_BASE_URL } from "../Apicongfig";
import CookieService from "../Utils/CookieService";

function PlatformFeedback() {
  const { t } = useTranslation("global");
  
  // Widget states
  const [isVisible, setIsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Form states
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [category, setCategory] = useState("general");
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Check feedback status on mount
  useEffect(() => {
    const isSubmittedLocal = localStorage.getItem("tektime_platform_feedback_submitted") === "true";
    const isDismissedLocal = localStorage.getItem("tektime_platform_feedback_dismissed") === "true";

    if (isSubmittedLocal) {
      // If already submitted, never show
      setIsVisible(false);
      return;
    }

    // Always show the widget (either as a button or open popup)
    setIsVisible(true);

    // If someone opened the dedicated feedback link (/tektime-feedback, or the
    // older ?feedback=open param), pop the form open immediately, even if this
    // browser had dismissed it before.
    const params = new URLSearchParams(window.location.search);
    const isDedicatedFeedbackRoute = window.location.pathname === "/tektime-feedback";
    if (isDedicatedFeedbackRoute || params.get("feedback") === "open") {
      setIsOpen(true);
      return;
    }

    if (!isDismissedLocal) {
      // If not dismissed before, pop it open automatically after a 5 second delay
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    // Mark as dismissed so we don't auto-open it on next page loads
    localStorage.setItem("tektime_platform_feedback_dismissed", "true");
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  // Copies a shareable link to a dedicated feedback route so whoever opens
  // it lands straight on the feedback form, regardless of which page they're on.
  const handleCopyLink = async (e) => {
    e.stopPropagation();
    const feedbackLink = `${window.location.origin}/tektime-feedback`;

    try {
      await navigator.clipboard.writeText(feedbackLink);
    } catch (error) {
      // Fallback for browsers/contexts where Clipboard API isn't available (e.g. no HTTPS)
      const textarea = document.createElement("textarea");
      textarea.value = feedbackLink;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand("copy");
      } catch (fallbackError) {
        console.warn("Copy to clipboard failed", fallbackError);
      }
      document.body.removeChild(textarea);
    }

    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      return;
    }

    setIsSubmitting(true);

    const token = CookieService.get("token");
    const userId = CookieService.get("user_id");

    try {
      // Try to send feedback to the platform-feedback API endpoint
      await axios.post(
        `${API_BASE_URL}/platform-feedback`,
        {
          user_id: userId || null,
          rating: rating,
          // category: category,
          comment: comment,
          source: "web_platform"
        },
        {
          headers: {
            Authorization: `Bearer ${token || ""}`,
          },
        }
      );
    } catch (error) {
      // Gracefully catch errors (e.g. 404 if API is not yet live on backend)
      console.warn("Platform feedback API failed or not found. Saving locally.", error);
    } finally {
      // Mark as submitted locally so we NEVER show it again
      localStorage.setItem("tektime_platform_feedback_submitted", "true");
      setIsSubmitted(true);
      setIsSubmitting(false);

      // Fade out and remove the widget entirely after 2.5 seconds of success view
      setTimeout(() => {
        setIsVisible(false);
      }, 2500);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="platform-feedback-container">
      {/* Floating Action Button (Shows when card is closed) */}
      {!isOpen && (
        <div className="platform-feedback-fab-group">
          <button
            className="platform-feedback-trigger"
            onClick={handleOpen}
            title={t("feedback.title", "Partagez votre avis")}
          >
            <MdFeedback size={20} className="feedback-icon-spin" />
            <span className="trigger-text">{t("feedback.triggerText", "Avis")}</span>
          </button>

          <button
            className="platform-feedback-copy-btn"
            onClick={handleCopyLink}
            title={t("feedback.copyLink", "Copier le lien du formulaire")}
          >
            {linkCopied ? (
              <MdCheck size={18} className="copy-success-icon" />
            ) : (
              <MdContentCopy size={18} />
            )}
          </button>
        </div>
      )}

      {/* Feedback Card/Popup */}
      {isOpen && (
        <div className="platform-feedback-card">
          <div className="feedback-card-header">
            <div className="header-info">
              <MdFeedback size={22} className="header-icon" />
              <div>
                <h4 className="header-title">{t("feedback.cardTitle", "Partagez votre expérience")}</h4>
                <p className="header-subtitle">
                  {t("feedback.cardSubtitle", "Aidez-nous à améliorer TekTime.")}
                </p>
              </div>
            </div>
            <div className="header-actions">
              <button
                className="feedback-copy-link-btn"
                onClick={handleCopyLink}
                title={t("feedback.copyLink", "Copier le lien du formulaire")}
                disabled={isSubmitting}
              >
                {linkCopied ? <MdCheck size={16} /> : <MdContentCopy size={16} />}
              </button>
              <button
                className="feedback-close-btn"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                <MdClose size={18} />
              </button>
            </div>
          </div>

          <div className="feedback-card-body">
            {isSubmitted ? (
              <div className="feedback-success-state animate__animated animate__fadeIn">
                <MdCheckCircle size={52} className="success-icon" />
                <h3>{t("feedback.thankYou", "Merci !")}</h3>
                <p>{t("feedback.thankYouDesc", "Votre avis a bien été envoyé. Merci pour votre retour !")}</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* Star Rating Section */}
                <div className="feedback-section">
                  <label className="section-label">{t("feedback.rateExperience", "Comment évaluez-vous TekTime ?")}</label>
                  <div className="star-rating-container">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`star-btn ${star <= (hoverRating || rating) ? "active" : ""}`}
                        onClick={() => setRating(star)}
                        onMouseEnter={() => setHoverRating(star)}
                        onMouseLeave={() => setHoverRating(0)}
                      >
                        {star <= (hoverRating || rating) ? (
                          <MdStar size={32} />
                        ) : (
                          <MdStarBorder size={32} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Category Selector */}
                {/* <div className="feedback-section">
                  <label className="section-label">{t("feedback.categoryLabel", "Select a Category")}</label>
                  <div className="category-pills">
                    {[
                      { id: "suggestion", label: t("feedback.catSuggestion", "Suggestion") },
                      { id: "question", label: t("feedback.catQuestion", "Question") },
                      { id: "praise", label: t("feedback.catPraise", "Praise") },
                      { id: "general", label: t("feedback.catGeneral", "General") }
                    ].map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        className={`category-pill ${category === cat.id ? "selected" : ""}`}
                        onClick={() => setCategory(cat.id)}
                      >
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div> */}

                {/* Comment Textarea */}
                <div className="feedback-section">
                  <label className="section-label">{t("feedback.commentLabel", "Dites-nous en plus")}</label>
                  <div className="textarea-container">
                    <textarea
                      className="feedback-textarea"
                      placeholder={t("feedback.textareaPlaceholder", "Ce qui vous plaît, ce qui pourrait être amélioré...")}
                      value={comment}
                      onChange={(e) => setComment(e.target.value.slice(0, 500))}
                      maxLength={500}
                      rows={3}
                    />
                    <span className="char-count">{comment.length}/500</span>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="feedback-submit-btn"
                  disabled={rating === 0 || isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="feedback-spinner" />
                  ) : (
                    t("feedback.submitBtn", "Envoyer mon avis")
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PlatformFeedback;